import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
// The package barrel (a pure re-export list; the executors behind it load
// their engines lazily): the repo forbids deep imports into @fias/* internals.
import {
  clearPluginLocalStoreExceptUser,
  drainAndClearPluginLocalStoreForUser,
} from '@fias/client-entities';
import { useQueryClient } from '@tanstack/react-query';
import {
  authApi,
  type AuthUser,
  type SignInResult,
  type RecoveryLoginResult,
} from '@/lib/auth-api';
import { notifySessionExpired } from '@/lib/auth-refresh';
import { isSessionRefreshingError, sessionRefreshingRetryDelayMs } from '@/lib/session-refreshing';
import { clearBootSessionHint } from '@/lib/boot-session-hint';
import { setTelemetryUser, clearTelemetryUser } from '@/lib/telemetry';
import { identifyIfNeeded } from '@/lib/attribution';

/** localStorage key written on an explicit sign-out so other tabs of this
 *  origin drop the identity (the `storage` event fires only in other tabs). */
const SIGNED_OUT_BROADCAST_KEY = 'fias:auth:signed-out';

/**
 * Guest Trial Mode: set when a guest confirms a signup, consumed on the next
 * login to finalize the Postgres side of the upgrade. sessionStorage bridges the
 * signup → login redirect (SignupPage sends the user to /auth/login, not an
 * auto-login). See authApi.completeGuestUpgrade.
 */
const GUEST_UPGRADE_PENDING_KEY = 'fias_guest_upgrade_pending';

/**
 * The fias_session cookie carries a Cognito ID token with a 60m TTL. Refresh
 * well before that so an actively-working user never sees a 401 mid-action. 45m
 * leaves a 15min margin for clock skew and request latency. We can't read the
 * cookie's actual issue time (httpOnly), so this is a cadence rather than a
 * precise deadline.
 *
 * This constant tracks `idTokenValidity` in infra/cdk/lib/auth-stack.ts — one
 * decision written in two files. They were out of step from 2026-07-28 (tokens
 * cut 3h -> 60m server-side, no client change) until 2026-09-17: the session
 * cookie was dead for 90 of every 150 minutes, so nearly every cold boot fell
 * through to the /auth/me recovery path in lib/auth-api.ts. If you change the
 * token lifetime, change this too.
 */
const SESSION_REFRESH_INTERVAL_MS = 45 * 60 * 1000;

/**
 * When the tab regains focus/visibility, re-validate the session if it's been at
 * least this long since the last refresh. Far shorter than the routine cadence so
 * a session that died while the user was away surfaces the "please sign in" prompt
 * the moment they come back, instead of letting them click around a logged-out app.
 */
const SESSION_REVALIDATE_ON_RETURN_MS = 60 * 1000;

/**
 * How many times the boot probe asks again while a Company portal's session
 * is still refreshing on the server (ADR-024) before this load gives up on an
 * answer. Each wait is the server's Retry-After (0.5–5 s), on top of the two
 * waits the probe itself makes — around fifteen seconds on the splash before
 * a server that cannot finish refreshing is treated like any other outage.
 */
const BOOT_SESSION_REFRESHING_RETRIES = 5;

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** True when the session is a Guest Trial Mode account (roles:['guest']). */
  isGuest: boolean;
  isLoading: boolean;
  /**
   * Password sign-in. For an MFA-enrolled user a correct password does NOT
   * establish a session — the result is `{ status: 'mfa_required' }` and the
   * caller must complete `mfaChallenge` (or `mfaRecoveryLogin`).
   */
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Mint an anonymous guest trial session (Guest Trial Mode). */
  createGuestSession: () => Promise<void>;
  /**
   * Answer the TOTP challenge after `signIn` returned `mfa_required`.
   */
  mfaChallenge: (code: string) => Promise<void>;
  /**
   * Sign in with a single-use recovery code. Spends ONE code and nothing else —
   * MFA stays enabled and the authenticator secret is unchanged.
   */
  mfaRecoveryLogin: (
    email: string,
    password: string,
    recoveryCode: string,
    opId: string,
  ) => Promise<RecoveryLoginResult>;
  /**
   * UX-only hint: whether the current session's token says MFA is enrolled.
   * Not authoritative — the MFA settings card reads live status separately.
   */
  mfaEnrolled: boolean;
  /**
   * Reset client auth state WITHOUT a `/auth/logout` round-trip, for flows
   * where the backend already revoked the session server-side (MFA enroll /
   * disable / support reset global sign-out) and the user must re-authenticate.
   */
  handleReauthRequired: () => void;
  /**
   * Cognito sign-up. `inviteCode` may be empty/omitted only for a guest whose
   * trial came from an ad click — the server (PreSignUp trigger) decides.
   */
  signUp: (
    email: string,
    password: string,
    inviteCode: string | undefined,
    username: string,
  ) => Promise<{ requiresConfirmation: boolean }>;
  confirmSignUp: (
    email: string,
    code: string,
    inviteCode?: string,
    username?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string, totpCode?: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  /**
   * Asks the server who is signed in, again — for a Company portal page that
   * finds the server saying "signed in" while this load's boot probe gave up
   * waiting for a session refresh (ADR-024). Settles the user either way; a
   * probe that still cannot answer leaves things as they are.
   */
  retryBootProbe: () => Promise<void>;
  /** Whether the app is running in local development mode */
  isDevMode: boolean;
  /** Available dev users (empty array if not in dev mode) */
  devUsers: AuthUser[];
  /** Switch to a different dev user by index. Only works in dev mode. */
  switchDevUser: (index: number) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Tracks whether the CURRENT session is a guest, so the (stable) signup/login
  // callbacks can read it without re-creating on every user change.
  const isGuestRef = useRef(false);
  useEffect(() => {
    isGuestRef.current = user?.roles?.includes('guest') ?? false;
  }, [user]);

  // Check for existing session on mount
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let sessionRefreshingWaits = 0;

    const initializeAuth = async () => {
      try {
        const currentUser = await authApi.getCurrentUser();
        if (cancelled) return;
        setUser(currentUser);
        if (currentUser) {
          setTelemetryUser(currentUser);
        }
      } catch (err) {
        if (cancelled) return;
        // A Company portal whose session is still refreshing on the server
        // (ADR-024): the person IS signed in, and the probe has already waited
        // its share. Stay on the splash and ask again when the server said to,
        // never render sign-in to them — only a 401/403 ends a portal session.
        // Bounded, so a server that cannot finish refreshing does not hold the
        // splash forever: past the bound this load reports no user with the
        // durable session hint intact, and the next load asks again.
        if (
          isSessionRefreshingError(err) &&
          sessionRefreshingWaits < BOOT_SESSION_REFRESHING_RETRIES
        ) {
          sessionRefreshingWaits += 1;
          retryTimer = setTimeout(() => void initializeAuth(), sessionRefreshingRetryDelayMs(err));
          return;
        }
        // No valid session
        setUser(null);
        clearTelemetryUser();
      }
      setIsLoading(false);
    };

    void initializeAuth();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
    };
  }, []);

  // Hand the prerendered snapshot's pre-JS "probably signed in" guess back to
  // the app now that the real answer is in. Until this runs the root element
  // still carries `data-fias-session-hint`, which hides `#root` behind a
  // loading splash — correct for the boot window, a blank page for the rest of
  // the session if it is never cleared. Runs on BOTH outcomes: signed in (the
  // redirect has been rendered) and signed out (the hint was stale and the
  // marketing site underneath must be revealed). See lib/boot-session-hint.ts.
  //
  // An effect, not a call inside `initializeAuth`'s `finally`: effects run
  // after the commit that carries `isLoading: false`, so the reveal can never
  // land a frame ahead of the render that decided what to reveal.
  useEffect(() => {
    if (!isLoading) clearBootSessionHint();
  }, [isLoading]);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    const result = await authApi.signIn(email, password);
    if (result.status === 'authenticated') {
      setUser(result.user);
      setTelemetryUser(result.user);
      // If this login follows a guest → full upgrade, finalize the Postgres side
      // (clear is_guest + grant the welcome). Fire-and-forget: idempotent, and a
      // failure just retries on the next login — never block sign-in on it.
      // Only when a session was actually established (mfa_required has none).
      if (sessionStorage.getItem(GUEST_UPGRADE_PENDING_KEY)) {
        sessionStorage.removeItem(GUEST_UPGRADE_PENDING_KEY);
        void authApi.completeGuestUpgrade().catch(() => {
          // Best-effort; the account is fully usable regardless.
        });
      }
    }
    // `mfa_required` establishes no session — leave `user` untouched; the
    // caller drives the challenge step and `mfaChallenge` sets the user.
    return result;
  }, []);

  const createGuestSession = useCallback(async () => {
    const result = await authApi.createGuestSession();
    setUser(result.user);
    setTelemetryUser(result.user);
  }, []);

  const mfaChallenge = useCallback(async (code: string) => {
    const { user: challengedUser } = await authApi.mfaChallenge(code);
    setUser(challengedUser);
    setTelemetryUser(challengedUser);
  }, []);

  const mfaRecoveryLogin = useCallback(
    async (
      email: string,
      password: string,
      recoveryCode: string,
      opId: string,
    ): Promise<RecoveryLoginResult> => {
      const result = await authApi.mfaRecoveryLogin(email, password, recoveryCode, opId);
      if (result.status === 'authenticated') {
        setUser(result.user);
        setTelemetryUser(result.user);
      }
      return result;
    },
    [],
  );

  const handleReauthRequired = useCallback(() => {
    authApi.clearLocalSession();
    setUser(null);
    clearTelemetryUser();
    // Drop all cached server-state so the next user (or the re-authenticated
    // session) never sees the previous identity's cached data — including
    // security-posture queries (MFA status) whose keys are not user-scoped.
    // gcTime would otherwise serve stale cross-user data on a
    // shared browser.
    queryClient.clear();
  }, [queryClient]);

  const signUp = useCallback(
    async (email: string, password: string, inviteCode: string | undefined, username: string) => {
      return authApi.signUp(email, password, inviteCode, username);
    },
    [],
  );

  const confirmSignUp = useCallback(
    async (email: string, code: string, inviteCode?: string, username?: string) => {
      // If a guest is confirming a signup, the server upgrades their existing
      // usr_ id in PostConfirmation (reading the guest cookie). Mark it so the
      // next login finalizes the Postgres side (SignupPage redirects to login
      // rather than auto-logging-in, so the flag bridges that navigation).
      const wasGuest = isGuestRef.current;
      await authApi.confirmSignUp(email, code, inviteCode, username);
      if (wasGuest) {
        sessionStorage.setItem(GUEST_UPGRADE_PENDING_KEY, '1');
      }
    },
    [],
  );

  const signOut = useCallback(async () => {
    const signingOut = user?.userId ?? null;
    try {
      await authApi.signOut();
    } finally {
      // The user asked to sign out, so this device signs out even when the
      // API call fails (offline, a 5xx): the local identity, its cached
      // server state and its device-local plugin data all go, and the error
      // still reaches the caller. In that case the server session may outlive
      // this — the next boot probe would find it — which is the same outcome
      // as before this block existed, minus the data left on the device.
      //
      // ADR-031: plugins keep working data (recovery journals, drafts) on this
      // device in the host's IndexedDB, namespaced by user. A session the user
      // chose to end must not leave it behind on a shared machine, so the
      // explicit sign-out waits for the clear (bounded — the store gives up
      // waiting after a few seconds rather than hold the sign-out). An
      // involuntary drop (expired cookie, failed refresh) deliberately keeps
      // it — the same user signing back in is what a recovery journal is for;
      // see the identity effect below for the other half of the guarantee.
      //
      // Tell every other tab of this origin FIRST (a `storage` event only
      // fires in other tabs) so they drop the identity and their plugins stop
      // writing under it before this tab clears — and each of them clears the
      // departed user too, which catches a write of theirs that was already
      // in flight.
      if (signingOut) {
        try {
          localStorage.setItem(
            SIGNED_OUT_BROADCAST_KEY,
            JSON.stringify({ userId: signingOut, at: Date.now() }),
          );
          // The event has fired in every other tab; leaving the key would keep
          // the departed user's id in this device's storage indefinitely.
          // (Removing it fires a second event with a null value, which the
          // listener ignores.)
          localStorage.removeItem(SIGNED_OUT_BROADCAST_KEY);
        } catch {
          // Storage unavailable (private mode, quota): other tabs learn on
          // their next refresh tick or 401 instead.
        }
        // Drain first: new plugin calls are refused from here, the ones in
        // flight settle, then the clear runs — nothing lands in between.
        await drainAndClearPluginLocalStoreForUser(signingOut);
      }
      setUser(null);
      clearTelemetryUser();
      // Clear all cached server-state on logout (see handleReauthRequired) so a
      // subsequent sign-in on the same browser starts from a clean cache.
      queryClient.clear();
    }
  }, [queryClient, user?.userId]);

  // ADR-031: device-local plugin data belongs to one identity. Whenever an
  // identity ARRIVES — sign-in, MFA completion, the boot probe finding a live
  // session — every namespace that is not its own is swept, so whatever
  // another user left behind (a session that expired with the tab closed, a
  // guest whose trial ended, a tab still writing after another tab signed
  // out) is gone before this user's plugins run. Nothing is cleared when an
  // identity DROPS involuntarily (401 re-auth, failed refresh): the same user
  // re-signing-in keeps their journal, and a different user triggers the
  // sweep. Only the explicit sign-out above clears on the way out. Best
  // effort and fire-and-forget: the helper never throws.
  const previousUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const next = user?.userId ?? null;
    if (previousUserIdRef.current === next) return;
    previousUserIdRef.current = next;
    if (next) void clearPluginLocalStoreExceptUser(next);
  }, [user?.userId]);

  const forgotPassword = useCallback(async (email: string) => {
    await authApi.forgotPassword(email);
  }, []);

  const confirmForgotPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      await authApi.confirmForgotPassword(email, code, newPassword);
    },
    [],
  );

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string, totpCode?: string) => {
      await authApi.changePassword(oldPassword, newPassword, totpCode);
      // changePassword now revokes the session in Cognito and clears local
      // storage. Reflect that in context so callers re-mount as logged-out
      // and the user is forced to re-authenticate with the new password.
      setUser(null);
    },
    [],
  );

  const retryBootProbe = useCallback(async () => {
    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser);
      if (currentUser) setTelemetryUser(currentUser);
    } catch {
      // Still refreshing, or a transient failure: nothing to conclude.
    }
  }, []);

  const refreshSession = useCallback(async () => {
    // Actually rotate the session cookie (POST /auth/refresh) — the
    // previous implementation only re-fetched /auth/me, which kept
    // the client's view of the user fresh but did nothing to extend
    // the underlying cookie's expiry.
    const ok = await authApi.refreshSession();
    if (!ok) {
      setUser(null);
      clearTelemetryUser();
      notifySessionExpired();
    }
  }, []);

  // Acquisition attribution: link the anonymous visitor id to the signed-in
  // user (once per user per browser — identifyIfNeeded self-guards and
  // retries on the next authenticated load if the POST fails).
  useEffect(() => {
    if (user?.userId) {
      void identifyIfNeeded(user.userId);
    }
  }, [user?.userId]);

  // Mirror any session-expiry signal into auth state. The api 401 interceptor,
  // streaming hooks, and the proactive refresh below all dispatch
  // `fias:session-expired` when a session can't be recovered; without reacting to
  // it here, a 401 from the api layer pops the "session expired" modal but leaves
  // `user` set — so ProtectedRoute keeps rendering and the app looks
  // logged-in-but-broken (you can click around, but every action silently fails).
  // Clearing `user` makes the whole app consistently reflect the logged-out state.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      clearTelemetryUser();
    };
    // A sign-out in another tab of this origin: the session is gone, so this
    // tab reflects it at once rather than on its next refresh tick (and its
    // plugins stop writing device-local data under the departed identity —
    // ADR-031).
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SIGNED_OUT_BROADCAST_KEY || e.newValue === null) return;
      onExpired();
      // An explicit sign-out elsewhere: drain and clear the departed user here
      // as well. Idempotent with the signing-out tab's own clear; the drain
      // refuses this tab's plugins from now on and waits for their calls in
      // flight, so nothing is left behind.
      let departed: string | null = null;
      try {
        departed = (JSON.parse(e.newValue) as { userId?: unknown })?.userId as string | null;
      } catch {
        departed = null;
      }
      if (typeof departed === 'string' && departed) {
        void drainAndClearPluginLocalStoreForUser(departed);
      }
    };
    window.addEventListener('fias:session-expired', onExpired);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('fias:session-expired', onExpired);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Proactive refresh while signed in. setInterval gets throttled or
  // suspended in background tabs by every modern browser, so we also
  // re-validate on focus/visibility when the tab returns. The user pays
  // no perceptible cost — refresh is a single cookie-only POST.
  useEffect(() => {
    if (!user) return;

    let lastRefreshAt = Date.now();

    const refreshNow = async () => {
      lastRefreshAt = Date.now();
      const ok = await authApi.refreshSession();
      if (!ok) {
        setUser(null);
        clearTelemetryUser();
        notifySessionExpired();
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshNow();
      }
      // If hidden, skip — the focus/visibility handler below catches us
      // up when the user returns to the tab.
    }, SESSION_REFRESH_INTERVAL_MS);

    // On returning to the tab/window, re-validate promptly so an expired
    // session surfaces the sign-in prompt immediately rather than after the
    // next routine refresh tick or a failed action. Throttled so rapid tab
    // switches don't spam refreshes.
    const revalidateOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshAt >= SESSION_REVALIDATE_ON_RETURN_MS) {
        void refreshNow();
      }
    };

    document.addEventListener('visibilitychange', revalidateOnReturn);
    window.addEventListener('focus', revalidateOnReturn);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', revalidateOnReturn);
      window.removeEventListener('focus', revalidateOnReturn);
    };
  }, [user]);

  const switchDevUser = useCallback(async (index: number) => {
    const newUser = await authApi.switchDevUser(index);
    if (newUser) {
      setUser(newUser);
      setTelemetryUser(newUser);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isGuest: user?.roles?.includes('guest') ?? false,
    isLoading,
    signIn,
    createGuestSession,
    mfaChallenge,
    mfaRecoveryLogin,
    mfaEnrolled: user?.mfaEnrolled ?? false,
    handleReauthRequired,
    signUp,
    confirmSignUp,
    signOut,
    forgotPassword,
    confirmForgotPassword,
    changePassword,
    refreshSession,
    retryBootProbe,
    isDevMode: authApi.isDevMode(),
    devUsers: authApi.getDevUsers(),
    switchDevUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
